import { entityService } from '@/services/EntityService';
import { httpClient } from '@/utils/httpClient';
import { outputReport, getOutputFormat, getPreOpenedWindow } from '@/utils/reportOutput';
import { getPersonLabels } from '@/hooks/usePersonLabels';
import { isUuid } from '@equip/utils';
import {
  buildClearedIndividualsReportHtml,
  type ClearedIndividualsItem,
  type ClearedIndividualsTemplateOptions,
} from '@/reports/templates/clearedIndividualsTemplate';

interface TransactionLogAsset {
  assetId: string;
  lin: string;
  nsn: string;
  nomenclature: string;
  transQty: number;
}

interface TransactionLogDetail {
  id: string;
  transactionType: string;
  memberName: string;
  memberId: string;
  dateProcessed: string;
  sourceWorkflow?: string;
  performedByName?: string;
  assets: TransactionLogAsset[];
}

interface TransactionLogResponse {
  transactions: TransactionLogDetail[];
}

interface EntityDataWithIndividualId {
  individual_id?: string;
  cleared_status?: boolean;
  cleared_at?: string | null;
}

interface MemberClearanceState {
  cleared: boolean;
  clearedAt: string | null;
}

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const normalizeDateOnly = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const normalizeIndividualId = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value
    .trim()
    .toUpperCase()
    .replace(/^INDV[-\s]*/, '')
    .replace(/\s+/g, '');
  return normalized.length > 0 ? normalized : null;
};

const parseIndividualIdFromEntityIdentifier = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  return normalizeIndividualId(trimmed.startsWith('INDV-') ? trimmed : `INDV-${trimmed}`);
};

const toIndividualEntityIdentifier = (normalizedIndividualId: string): string =>
  `INDV-${normalizedIndividualId}`;

const buildEqOrInFilter = (values: string[]): string => {
  if (values.length === 1) {
    return `eq.${values[0]}`;
  }
  return `in.(${values.join(',')})`;
};

const toTitle = (value: string): string =>
  value
    .toLowerCase()
    .split(/[_\s]+/g)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');

const isExchangeWorkflow = (transaction: TransactionLogDetail): boolean => {
  const source = transaction.sourceWorkflow?.toLowerCase() ?? '';
  return source.includes('exchange') || source.includes('dx');
};

const formatTransactionTypeLabel = (transaction: TransactionLogDetail): string => {
  const type = transaction.transactionType?.toUpperCase?.() ?? '';
  const exchange = isExchangeWorkflow(transaction);

  if (exchange && type === 'TURN_IN') return 'Dx/Turn In';
  if (exchange && type === 'ISSUE_OUT') return 'Dx/Issue';
  if (type === 'TURN_IN') return 'Turn In';
  if (type === 'NON_RECOVERABLE_CLEAR') return 'Non-Recoverable';
  if (type === 'ISSUE_OUT') return 'Issue';
  if (type === 'EXCHANGE') return 'DX';
  if (type === 'INDIVIDUAL_CLEARED') return 'Cleared';
  return type ? toTitle(type) : '—';
};

const parseCatalogUnitCost = (
  entityData: Record<string, unknown> | null | undefined,
): number | null => {
  const raw = entityData?.unit_price;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === 'string') {
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const normalizeMemberStatusFilter = (value: unknown): 'cleared' | 'active' | 'all' => {
  if (typeof value !== 'string') {
    return 'all';
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'cleared') return 'cleared';
  if (normalized === 'active') return 'active';
  return 'all';
};

export const runClearedIndividualsReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  const fromDate = normalizeDateOnly(params.fromDate);
  const toDate = normalizeDateOnly(params.toDate);
  const personParam = params.person as { id: string; displayName: string } | null | undefined;
  const individualName = personParam?.displayName ?? '';
  const memberStatusFilter = normalizeMemberStatusFilter(params.memberStatus);

  const query = new URLSearchParams({
    limit: '2000',
    types: 'TURN_IN,ISSUE_OUT,EXCHANGE,NON_RECOVERABLE_CLEAR,INDIVIDUAL_CLEARED',
  });
  if (fromDate) query.set('from', fromDate);
  if (toDate) query.set('to', toDate);

  const response = await httpClient.get<TransactionLogResponse>(
    `/api/rest/app/v1/transactions/log?${query.toString()}&source=report`,
  );
  const transactions = response.transactions ?? [];

  const memberFiltered = transactions.filter((transaction) => {
    if (!transaction.memberId || !transaction.memberName) return false;
    if (!individualName) return true;
    return transaction.memberName.toLowerCase().includes(individualName.toLowerCase());
  });

  const memberIds = Array.from(
    new Set(memberFiltered.map((tx) => tx.memberId).filter((memberId) => isUuid(memberId))),
  );

  const individualIdByMemberId = new Map<string, string>();
  for (const batchIds of chunk(memberIds, 75)) {
    const idFilter = buildEqOrInFilter(batchIds);
    const people = await entityService.list({
      type: 'person',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const person of people) {
      const entityData = (person.entity_data ?? {}) as EntityDataWithIndividualId;
      const individualId = normalizeIndividualId(entityData.individual_id);
      if (individualId) {
        individualIdByMemberId.set(person.id, individualId);
      }
    }
  }

  const clearanceStateByIndividualId = new Map<string, MemberClearanceState>();
  const individualIds = Array.from(new Set(Array.from(individualIdByMemberId.values())));

  for (const batchIndividualIds of chunk(individualIds, 75)) {
    const stateIdentifiers = batchIndividualIds.map((individualId) =>
      toIndividualEntityIdentifier(individualId),
    );
    const stateFilter = buildEqOrInFilter(stateIdentifiers);
    const states = await entityService.list({
      type: 'individual_state',
      limit: stateIdentifiers.length,
      order: 'id.asc',
      filters: {
        entity_identifier: stateFilter,
        select: 'entity_identifier,entity_data',
      },
    });

    for (const state of states) {
      const entityData = (state.entity_data ?? {}) as EntityDataWithIndividualId;
      const individualId =
        parseIndividualIdFromEntityIdentifier(state.entity_identifier) ??
        normalizeIndividualId(entityData.individual_id);
      if (!individualId) continue;
      clearanceStateByIndividualId.set(individualId, {
        cleared: entityData.cleared_status === true,
        clearedAt: typeof entityData.cleared_at === 'string' ? entityData.cleared_at : null,
      });
    }
  }

  const clearanceByMemberId = new Map<string, MemberClearanceState>();
  for (const memberId of memberIds) {
    const individualId = individualIdByMemberId.get(memberId);
    const state = individualId ? clearanceStateByIndividualId.get(individualId) : null;
    clearanceByMemberId.set(memberId, {
      cleared: state?.cleared === true,
      clearedAt: state?.clearedAt ?? null,
    });
  }

  // If an INDIVIDUAL_CLEARED transaction exists for a member, treat them as
  // cleared regardless of whether the Individual ID lookup succeeded.
  for (const tx of memberFiltered) {
    if (tx.transactionType?.toUpperCase() === 'INDIVIDUAL_CLEARED') {
      const existing = clearanceByMemberId.get(tx.memberId);
      if (!existing || !existing.cleared) {
        clearanceByMemberId.set(tx.memberId, {
          cleared: true,
          clearedAt: existing?.clearedAt ?? tx.dateProcessed ?? null,
        });
      }
    }
  }

  const statusFiltered =
    memberStatusFilter === 'all'
      ? memberFiltered
      : memberFiltered.filter((tx) => {
          const memberState = clearanceByMemberId.get(tx.memberId);
          const isCleared = memberState?.cleared === true;
          return memberStatusFilter === 'cleared' ? isCleared : !isCleared;
        });

  // Fetch IE kit name by member+LIN from ie_summary
  const ieMemberIds = Array.from(
    new Set(
      statusFiltered
        .map((tx) => tx.memberId)
        .filter((id): id is string => typeof id === 'string' && isUuid(id)),
    ),
  );

  const ieKitNameByMemberLin = new Map<string, string>();
  for (const batchIds of chunk(ieMemberIds, 75)) {
    const personIdFilter =
      batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;
    const ieQuery = new URLSearchParams({
      person_id: personIdFilter,
      select: 'person_id,lin,kit_name',
      limit: String(Math.max(batchIds.length * 100, 500)),
    });

    const rows = await httpClient.get<
      Array<{ person_id?: string; lin?: string | null; kit_name?: string | null }>
    >(`/api/rest/data/v1/ie_summary?${ieQuery.toString()}`);
    for (const row of rows) {
      const memberId = row.person_id ?? '';
      const lin = row.lin?.trim().toUpperCase() ?? '';
      const kitName = row.kit_name?.trim() ?? '';
      if (memberId && lin && kitName) {
        ieKitNameByMemberLin.set(`${memberId}|${lin}`, kitName);
      }
    }
  }

  const assetIds = Array.from(
    new Set(
      statusFiltered
        .flatMap((tx) => tx.assets ?? [])
        .map((asset) => asset.assetId)
        .filter((id): id is string => typeof id === 'string' && id.trim().length > 0),
    ),
  );

  const catalogIdByAssetId = new Map<string, string | null>();
  const catalogIds: string[] = [];

  for (const batchIds of chunk(assetIds, 75)) {
    const idFilter = buildEqOrInFilter(batchIds);
    const assets = await entityService.list({
      type: 'asset',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const asset of assets) {
      const entityData = (asset.entity_data ?? {}) as Record<string, unknown>;
      const rawCatalogId = entityData.catalog_id;
      const catalogId = typeof rawCatalogId === 'string' ? rawCatalogId.trim() : '';
      const normalized = catalogId.length > 0 ? catalogId : null;
      catalogIdByAssetId.set(asset.id, normalized);
      if (normalized) catalogIds.push(normalized);
    }
  }

  const unitCostByCatalogId = new Map<string, number | null>();
  const uniqueCatalogIds = Array.from(new Set(catalogIds));
  for (const batchIds of chunk(uniqueCatalogIds, 75)) {
    const idFilter = buildEqOrInFilter(batchIds);
    const catalogs = await entityService.list({
      type: 'catalog',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const catalog of catalogs) {
      const entityData = (catalog.entity_data ?? {}) as Record<string, unknown>;
      unitCostByCatalogId.set(catalog.id, parseCatalogUnitCost(entityData));
    }
  }

  const items: ClearedIndividualsItem[] = statusFiltered.flatMap(
    (transaction): ClearedIndividualsItem[] => {
      const memberState = clearanceByMemberId.get(transaction.memberId);
      const clearedDate = normalizeDateOnly(memberState?.clearedAt);
      const processedDate = transaction.dateProcessed
        ? (transaction.dateProcessed.split('T')[0] ?? '')
        : '';
      const user = transaction.performedByName ?? '—';
      const transactionTypeLabel = formatTransactionTypeLabel(transaction);

      const assets = transaction.assets ?? [];

      // INDIVIDUAL_CLEARED transactions have no assets — still emit a row
      // showing the member was cleared.
      if (assets.length === 0) {
        return [
          {
            individual: transaction.memberName ?? null,
            clearedDate,
            transactionTypeLabel,
            ie: null,
            lin: null,
            nsn: null,
            nomenclature: '—',
            quantity: null,
            totalLineCost: null,
            date: processedDate,
            user,
          },
        ];
      }

      return assets.map((asset) => {
        const catalogId = catalogIdByAssetId.get(asset.assetId) ?? null;
        const unitCost = catalogId ? (unitCostByCatalogId.get(catalogId) ?? null) : null;
        const qty = asset.transQty ?? 1;
        const totalLineCost =
          typeof unitCost === 'number' && Number.isFinite(unitCost) ? unitCost * qty : null;

        return {
          individual: transaction.memberName ?? null,
          clearedDate,
          transactionTypeLabel,
          ie: (() => {
            const lin = asset.lin === '—' ? null : asset.lin?.trim().toUpperCase();
            if (!lin) return null;
            return ieKitNameByMemberLin.get(`${transaction.memberId}|${lin}`) ?? null;
          })(),
          lin: asset.lin === '—' ? null : asset.lin,
          nsn: asset.nsn === '—' ? null : asset.nsn,
          nomenclature: asset.nomenclature || '—',
          quantity: qty,
          totalLineCost,
          date: processedDate,
          user,
        };
      });
    },
  );

  const totalValue = items.reduce((sum, item) => {
    if (typeof item.totalLineCost === 'number' && Number.isFinite(item.totalLineCost)) {
      return sum + item.totalLineCost;
    }
    return sum;
  }, 0);

  const ieKitLabel = (params.ieKitLabel as string | undefined) ?? 'IE Kit';

  const options: ClearedIndividualsTemplateOptions = {
    personLabels: getPersonLabels(params),
    generatedAt: new Date(),
    header: {
      fromDate,
      toDate,
      individualName: individualName || null,
      memberStatus: memberStatusFilter === 'all' ? null : memberStatusFilter,
      totalValue: totalValue > 0 ? totalValue : null,
    },
    items,
    ieKitLabel,
  };

  const html = buildClearedIndividualsReportHtml(options);
  outputReport(
    html,
    'cleared-individuals-report.html',
    getOutputFormat(params),
    getPreOpenedWindow(params),
  );
};
